import type { Project, ProjectVideo, SiteData, TeamMember, Video } from '../core/types';
import { getCurrentLanguage, translate } from './language';
import { openProjectModal, openVideoModal } from './modals';

const MAX_FEATURED_VIDEOS = 6;

const createTeamCard = (member: TeamMember): string => {
  const linkedin = member.linkedin
    ? `<a href="${member.linkedin}" class="team-card__linkedin" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">in</a>`
    : '';
  return `
    <article class="team-card">
      ${linkedin}
      <img class="team-card__photo" src="${member.photo}" alt="${member.name}" loading="lazy" />
      <div class="team-card__info">
        <h3 class="team-card__name">${member.name}</h3>
        <p class="team-card__position">${member.position}</p>
      </div>
    </article>
  `;
};

const createProjectCard = (project: Project): string => {
  const localizedProject = localizeProject(project);
  return `
    <article class="project-card" data-project="${encodeURIComponent(JSON.stringify(localizedProject))}">
      <img class="project-card__cover" src="${localizedProject.cover}" alt="${localizedProject.title}" loading="lazy" />
      <div class="project-card__content">
        <h3 class="project-card__title">${localizedProject.title}</h3>
        <p class="project-card__description">${localizedProject.shortDescription}</p>
        <span class="project-card__role">${localizedProject.role}</span>
      </div>
    </article>
  `;
};

const createVideoCard = (video: Video): string => {
  return `
    <article class="video-card" data-video-url="${video.url}">
      <img class="video-card__preview" src="${video.preview}" alt="${video.title || 'Video'}" loading="lazy" />
      <div class="video-card__play"></div>
      ${video.title ? `<div class="video-card__title">${video.title}</div>` : ''}
    </article>
  `;
};

const getFeaturedVideos = (projects: Project[]): Video[] => {
  return projects
    .flatMap((project) => {
      const localizedProject = localizeProject(project);
      return (localizedProject.videos ?? [])
        .filter((video) => video.featured)
        .map((video) => ({
          preview: localizedProject.cover,
          url: video.url,
          title: `${localizedProject.title} — ${video.title}`,
        }));
    })
    .slice(0, MAX_FEATURED_VIDEOS);
};

const localizeProject = (project: Project): Project => {
  const translation = project.translations?.[getCurrentLanguage()];
  if (!translation) return project;

  return {
    ...project,
    title: translation.title ?? project.title,
    shortDescription: translation.shortDescription ?? project.shortDescription,
    role: translation.role ?? project.role,
    fullDescription: translation.fullDescription ?? project.fullDescription,
    tags: translation.tags ?? project.tags,
    videos: project.videos?.map((video, index) => ({
      ...video,
      title: translation.videos?.[index]?.title ?? video.title,
      type: translation.videos?.[index]?.type ?? video.type,
    })),
  };
};

const createProjectVideoButton = (video: ProjectVideo): string => {
  const watchLabel = translate('modal.watchVideo') || 'Watch video';
  const meta = video.type ? `<span class="modal__project-video-type">${video.type}</span>` : '';
  return `
    <button class="modal__project-video" type="button" data-project-video-url="${video.url}">
      <span class="modal__project-video-title">${video.title}</span>
      ${meta}
      <span class="modal__project-video-action">${watchLabel}</span>
    </button>
  `;
};

const renderTeam = (team: TeamMember[]): void => {
  const grid = document.getElementById('team-grid');
  if (!grid) return;
  grid.innerHTML = team.map(createTeamCard).join('');
};

const renderProjects = (projects: Project[]): void => {
  const grid = document.getElementById('projects-grid');
  if (!grid) return;
  grid.innerHTML = projects.map(createProjectCard).join('');

  grid.onclick = (event) => {
    const card = (event.target as HTMLElement).closest('.project-card') as HTMLElement | null;
    if (!card) return;
    const data = card.getAttribute('data-project');
    if (!data) return;
    const project: Project = JSON.parse(decodeURIComponent(data));
    const roleLabel = translate('modal.projectRole') || 'Role';
    const tagsLabel = translate('modal.projectTags') || 'Tags';
    const videosLabel = translate('modal.projectVideos') || 'Videos';
    const projectLinkLabel = translate('modal.projectLink') || 'Open project';
    const screenshots = project.screenshots
      .map((screenshot) => `
        <div class="modal__slide">
          <img src="${screenshot}" alt="${project.title} screenshot" loading="lazy" />
        </div>
      `)
      .join('');
    const tags = project.tags.map((tag) => `<span class="modal__tag">${tag}</span>`).join('');
    const videos = project.videos?.length
      ? `
        <div class="modal__project-videos">
          <h3>${videosLabel}</h3>
          <div class="modal__project-video-list">
            ${project.videos.map(createProjectVideoButton).join('')}
          </div>
        </div>
      `
      : '';
    const projectLink = project.link
      ? `<a class="modal__link" href="${project.link}" target="_blank" rel="noopener noreferrer">${projectLinkLabel}</a>`
      : '';
    const content = `
      <div class="modal__slider">${screenshots}</div>
      <h2>${project.title}</h2>
      <p><strong>${roleLabel}:</strong> ${project.role}</p>
      <p>${project.fullDescription}</p>
      <div class="modal__tags"><strong>${tagsLabel}:</strong> ${tags}</div>
      ${videos}
      ${projectLink}
    `;
    openProjectModal(content);
  };
};

const renderVideos = (videos: Video[]): void => {
  const grid = document.getElementById('videos-grid');
  if (!grid) return;
  grid.innerHTML = videos.map(createVideoCard).join('');

  grid.onclick = (event) => {
    const card = (event.target as HTMLElement).closest('.video-card') as HTMLElement | null;
    if (!card) return;
    const url = card.getAttribute('data-video-url');
    if (url) openVideoModal(url);
  };
};

export const renderSections = (data: SiteData): void => {
  const renderLocalizedContent = (): void => {
    renderTeam(data.team);
    renderProjects(data.projects);
    renderVideos(getFeaturedVideos(data.projects));
  };

  renderLocalizedContent();
  document.addEventListener('vajaja:language-change', renderLocalizedContent);

  document.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('[data-project-video-url]') as HTMLElement | null;
    if (!button) return;
    const url = button.getAttribute('data-project-video-url');
    if (url) openVideoModal(url);
  });
};
